/**
 * src/components/shared/FilterBar.jsx — Generalized filter toolbar
 * Reusable across views (Gantt, Dashboard, Board, etc.)
 *
 * Phase B: supports a `dimensions` prop for schema-driven rendering.
 * When `dimensions` is provided, boolean-prop rendering is bypassed
 * and the filter bar is built entirely from the dimension schema array.
 * When `dimensions` is null/undefined, existing behavior is unchanged.
 *
 * Sub-components extracted to ./filters/ for composability.
 */

import { useMemo } from 'react';
import { useT } from '../../lib/i18n/I18nContext';
import { TASK_STATUSES, taskStatusLabel } from '../../lib/constants/taskStatus';

import SearchInput from './filters/SearchInput';
import SelectFilter from './filters/SelectFilter';
import ChipFilter from './filters/ChipFilter';
import TabFilter from './filters/TabFilter';
import ToggleFilter from './filters/ToggleFilter';
import ClearButton from './filters/ClearButton';
import LegacyFilterBar from './filters/LegacyFilterBar';

/* ─── Constants ─── */

// Use canonical statuses from taskStatus.js — filter out non-actionable ones for default UI
const DEFAULT_STATUSES = TASK_STATUSES.filter(s => s !== 'review' && s !== 'cancelled');

// Display labels derived from the canonical taskStatusLabel() function
const STATUS_LABELS = Object.fromEntries(
  TASK_STATUSES.map(s => [s, taskStatusLabel(s)])
);

// Production stage labels for bundle filtering
const BUNDLE_STAGE_SLUGS = ['todo', 'in_progress', 'review', 'done'];
const BUNDLE_STAGE_LABELS = BUNDLE_STAGE_SLUGS.map(s => taskStatusLabel(s));

export default function FilterBar({
  filters = {},
  onFilterChange,
  onClearAll,
  projects = [],
  people = [],
  bundles = [],
  schemaOptions = {},
  showGroupBy = false,
  showBundle = false,
  showBundleStage = false,
  showDepartment = false,
  showSearch = false,
  showProjectType = false,
  showStage = false,
  showVessel = false,
  vesselOptions = [],
  // Phase B: dimension-driven rendering
  dimensions = null,
  tabCounts = null,
  // Phase B1: optional hidden-by-filter bundle count hint
  hiddenBundleCount = 0,
}) {
  const { t } = useT();
  const {
    stages: allStages = [],
    departments = [],
    statuses: rawStatuses = [],
    projectTypes = [],
    stagesByProjectType = {},
  } = schemaOptions;

  const statuses = rawStatuses.length > 0 ? rawStatuses : DEFAULT_STATUSES;

  // When a projectType is selected, narrow stages to only those for that type
  const stages = useMemo(() => {
    if (!filters.projectType) return allStages;
    const allowedIds = stagesByProjectType[filters.projectType] || [];
    if (!allowedIds.length) return allStages;
    return allStages.filter((s) => {
      const id = typeof s === 'string' ? s : s?.id;
      return allowedIds.includes(id);
    });
  }, [allStages, filters.projectType, stagesByProjectType]);

  // Count active filters for the mobile badge
  const activeFilterCount = [
    filters.status,
    filters.bundleStage,
    filters.assignee,
    filters.project,
    filters.projectType,
    filters.stage,
    filters.department,
    filters.bundle,
    filters.vessel,
    filters.search,
    filters.groupBy && filters.groupBy !== 'project' ? filters.groupBy : null,
  ].filter(Boolean).length;

  const hasActiveFilter = activeFilterCount > 0;

  /* ─── Phase B: dimension-driven rendering ────────────────────────── */
  if (dimensions) {
    return <DimensionBar dimensions={dimensions} filters={filters} onFilterChange={onFilterChange} onClearAll={onClearAll} tabCounts={tabCounts} />;
  }

  /* ─── Legacy boolean-prop rendering (delegated) ────────────────── */
  return (
    <LegacyFilterBar
      filters={filters}
      onFilterChange={onFilterChange}
      onClearAll={onClearAll}
      projects={projects}
      people={people}
      bundles={bundles}
      statuses={statuses}
      statusLabels={STATUS_LABELS}
      bundleStageLabels={BUNDLE_STAGE_LABELS}
      stages={stages}
      departments={departments}
      projectTypes={projectTypes}
      showGroupBy={showGroupBy}
      showBundle={showBundle}
      showBundleStage={showBundleStage}
      showDepartment={showDepartment}
      showSearch={showSearch}
      showProjectType={showProjectType}
      showStage={showStage}
      showVessel={showVessel}
      vesselOptions={vesselOptions}
      hasActiveFilter={hasActiveFilter}
      activeFilterCount={activeFilterCount}
      hiddenBundleCount={hiddenBundleCount}
    />
  );
}

/* ─── DimensionBar — Phase B dimension-driven rendering ────────── */

function DimensionBar({ dimensions, filters, onFilterChange, onClearAll, tabCounts }) {
  const hasDimActive = dimensions.some((dim) => {
    const val = filters[dim.key];
    return val != null && val !== '' && val !== dim.default;
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: 'var(--glass)',
        borderRadius: 12,
        border: '1px solid var(--bdr)',
        flexWrap: 'wrap',
      }}
    >
      {dimensions.map((dim) => {
        const val = filters[dim.key] ?? dim.default ?? '';
        const opts = dim.optionsFn ? dim.optionsFn(filters) : dim.options;
        switch (dim.type) {
          case 'search':
            return <SearchInput key={dim.key} value={val} onChange={(v) => onFilterChange(dim.key, v)} placeholder={dim.placeholder} />;
          case 'select':
            return <SelectFilter key={dim.key} value={val} onChange={(v) => onFilterChange(dim.key, v)} options={opts} label={dim.labelKey} />;
          case 'chips':
            return <ChipFilter key={dim.key} value={val} onChange={(v) => onFilterChange(dim.key, v)} options={opts} />;
          case 'tabs':
            return <TabFilter key={dim.key} value={val} onChange={(v) => onFilterChange(dim.key, v)} options={dim.options} tabCounts={tabCounts} />;
          case 'toggle':
            return <ToggleFilter key={dim.key} value={val} onChange={(v) => onFilterChange(dim.key, v)} options={dim.options} />;
          default:
            return null;
        }
      })}
      {hasDimActive && onClearAll && <ClearButton onClick={onClearAll} label="Clear" />}
    </div>
  );
}
